import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { leaguesAPI } from '../services/apiService';

const NotificationContext = createContext(null);

export function NotificationProvider({ children, leagueId }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // getNotificationCount returns response.data directly (already unwrapped in apiService)
  const fetchUnreadCount = useCallback(async () => {
    if (!leagueId) return;
    try {
      const res = await leaguesAPI.getNotificationCount(leagueId);
      setUnreadCount(res?.unread_count || 0);
    } catch (e) {
      // Non-fatal — badge just shows stale count
    }
  }, [leagueId]);

  // getNotifications returns response.data directly
  const fetchNotifications = useCallback(async () => {
    if (!leagueId) return;
    setLoading(true);
    try {
      const res = await leaguesAPI.getNotifications(leagueId);
      setNotifications(res?.notifications || []);
    } catch (e) {
      // Non-fatal
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  // Mark individual notification read — calls markNotificationRead added to apiService
  const markRead = useCallback(async (notifId) => {
    if (!leagueId) return;
    try {
      await leaguesAPI.markNotificationRead(leagueId, notifId);
    } catch (e) {
      // Non-fatal — optimistic update applied below regardless
    }
    fetchUnreadCount();
    setNotifications(prev => prev.map(n =>
      n.notification_id === notifId ? { ...n, is_read: true } : n
    ));
  }, [leagueId, fetchUnreadCount]);

  // markNotificationsRead returns response.data directly
  const markAllRead = useCallback(async () => {
    if (!leagueId) return;
    try {
      await leaguesAPI.markNotificationsRead(leagueId);
    } catch (e) {
      // Non-fatal
    }
    setUnreadCount(0);
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
  }, [leagueId]);

  const toggleOpen = useCallback(() => {
    setIsOpen(prev => {
      if (!prev) fetchNotifications();
      return !prev;
    });
  }, [fetchNotifications]);

  // Poll every 60 seconds
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  return (
    <NotificationContext.Provider value={{
      unreadCount,
      notifications,
      isOpen,
      loading,
      toggleOpen,
      markRead,
      markAllRead,
      refresh: fetchNotifications,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error(
    'useNotifications must be used within NotificationProvider'
  );
  return ctx;
};
